import {Button, DataStatus, openDialog} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';

import {isFalse, isNotNilOrEmpty} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';
import {
  GetMetadaServiceCaseApiResponse,
  GetServiceCaseOrdersApiResponse,
  useGetServiceCaseActivityQuery,
  useGetServiceCaseUpcomingTasksQuery,
} from '@dms/api/workshop';
import {
  ActivitySidebarSendMessage,
  LogContactDialog,
  transformTaskToActivity,
} from '@dms/features/activity-panel';
import {openCreateTaskFormDialog} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {getServiceCaseContactPhoneNumber} from '../utils/getServiceCaseContactPhoneNumber';
import {ServiceCaseActivityList} from './ServiceCaseActivityList';

interface ServiceCaseActivityTabProps {
  serviceCaseId: string;
  serviceCase: GetMetadaServiceCaseApiResponse;
  serviceOrders: GetServiceCaseOrdersApiResponse;
}

export function ServiceCaseActivityTab(props: ServiceCaseActivityTabProps) {
  const [canReadTasks, canCreateTasks, canCreateServiceCaseActivity] = usePermissions({
    permissionKeys: ['readTask', 'createTask', 'serviceCaseActivityCreate'],
    scopes: {
      serviceCaseActivityCreate: {
        // BE Handles SC ACL
        branchId: 'SKIP',
        // BE Handles SC ACL
        serviceCaseType: 'SKIP',
      },
    },
  });

  const {
    data: activities,
    isLoading: isActivityLoading,
    isError: isActivityError,
  } = useGetServiceCaseActivityQuery({serviceCaseId: props.serviceCaseId}, {refetchOnFocus: true});
  const {
    data: upcomingTasks,
    isLoading: isUpcomingTasksLoading,
    isError: isUpcomingTasksError,
  } = useGetServiceCaseUpcomingTasksQuery({serviceCaseId: props.serviceCaseId});

  const customerId =
    props.serviceCase?.contactInformation?.customerId ?? props.serviceCase?.customerId;
  const defaultPhoneNumber = getServiceCaseContactPhoneNumber(
    props.serviceCase?.contactInformation?.phoneNumber?.prefix,
    props.serviceCase?.contactInformation?.phoneNumber?.number
  );
  const isAddNewTaskEnabled = canReadTasks && canCreateTasks;
  const isLoading = isActivityLoading || isUpcomingTasksLoading;
  const isError = isActivityError || isUpcomingTasksError;

  const handleNewTaskClick = () => {
    openCreateTaskFormDialog({
      'data-testid': 'service-case-activity-tab-create-task',
      relatedRecordId: props.serviceCaseId,
      resourceId: EntityResourceIds.serviceCase,
    });
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <VStack spacing={4} grow={1} minHeight={0}>
        <HStack spacing={2} wrap>
          <ActivitySidebarSendMessage
            resourceId={EntityResourceIds.serviceCase}
            recordId={props.serviceCaseId}
            customerId={customerId}
            secondaryRecordIds={[]}
            vehicleId={props.serviceCase?.vehicleId ?? undefined}
            defaultEmailAddress={props.serviceCase?.contactInformation?.email}
            defaultPhoneNumber={defaultPhoneNumber}
          />

          <Button
            leftIcon="content/add_circle"
            title={i18n.t('entity.task.actions.createTask')}
            variant="link"
            size="small"
            isDisabled={isFalse(isAddNewTaskEnabled)}
            onClick={handleNewTaskClick}
          />

          <Show when={canCreateServiceCaseActivity}>
            <Button
              leftIcon="communication/phone"
              title={i18n.t('entity.activityPanel.activity.logCall')}
              onClick={() =>
                openDialog(<LogContactDialog serviceCaseId={props.serviceCaseId} />, {
                  id: 'LOG_CONTACT_DIALOG',
                  title: i18n.t('entity.activityPanel.logContact.createLog'),
                  scrollBehavior: 'outside',
                })
              }
              variant="link"
              size="small"
            />
          </Show>
        </HStack>

        <Box flex={1} overflow="auto" minHeight={0}>
          <VStack spacing={4}>
            <ServiceCaseActivityList
              showEmpty
              label={i18n.t('entity.activityPanel.activity.nextSteps')}
              items={(upcomingTasks ?? []).map(transformTaskToActivity)}
              contactName={props.serviceCase?.contactInformation?.name}
              serviceCaseNumber={props.serviceCase?.number}
              serviceOrders={props.serviceOrders ?? []}
            />

            <Show when={isNotNilOrEmpty(activities)}>
              <ServiceCaseActivityList
                label={i18n.t('entity.activityPanel.activity.pastActivities')}
                items={activities ?? []}
                contactName={props.serviceCase?.contactInformation?.name}
                serviceCaseNumber={props.serviceCase?.number}
                serviceOrders={props.serviceOrders ?? []}
              />
            </Show>
          </VStack>
        </Box>
      </VStack>
    </DataStatus>
  );
}
